import React from 'react';
import { useAvatar } from '../../hooks/useAvatar';
import { getInitials } from '../../utils/avatarUtils';

interface UserAvatarProps {
  userId: string;
  name: string;
  avatarBase64?: string;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const sizeClasses = {
  sm: 'h-6 w-6 text-xs',
  md: 'h-8 w-8 text-sm',
  lg: 'h-12 w-12 text-base'
};

export const UserAvatar: React.FC<UserAvatarProps> = ({
  userId,
  name,
  avatarBase64,
  size = 'md',
  className = ''
}) => {
  const { avatarUrl, isLoading } = useAvatar({ userId, avatarBase64 });

  // Se não há avatarBase64 ou está vazio, mostrar iniciais
  if (!avatarBase64 || avatarBase64.trim() === '' || avatarBase64 === 'null' || avatarBase64 === 'undefined') {
    return (
      <div className={`${sizeClasses[size]} rounded-full bg-gray-300 flex items-center justify-center ${className}`}>
        <span className="font-medium text-gray-600">
          {getInitials(name)}
        </span>
      </div>
    );
  }

  // Se está carregando, mostrar placeholder
  if (isLoading) {
    return (
      <div className={`${sizeClasses[size]} rounded-full bg-gray-200 flex items-center justify-center ${className}`}>
        <div className="animate-pulse bg-gray-300 rounded-full h-4 w-4"></div>
      </div>
    );
  }

  // Se tem avatarUrl válida, mostrar imagem
  if (avatarUrl && avatarUrl.trim() !== '') {
    return (
      <img
        src={avatarUrl}
        alt={name}
        className={`${sizeClasses[size]} rounded-full object-cover ${className}`}
        onError={(e) => {
          // Se der erro, mostrar as iniciais
          e.currentTarget.style.display = 'none';
          const fallbackDiv = e.currentTarget.nextElementSibling as HTMLElement;
          if (fallbackDiv) {
            fallbackDiv.style.display = 'flex';
          }
        }}
      />
    );
  }

  // Fallback para iniciais
  return (
    <div className={`${sizeClasses[size]} rounded-full bg-gray-300 flex items-center justify-center ${className}`}>
      <span className="font-medium text-gray-600">
        {getInitials(name)}
      </span>
    </div>
  );
};
